import type { ErrorCode } from '@volee/api-client'

// The API error catalogue, SOURCE language. `Record<ErrorCode, string>` is the guarantee: a
// code added on the backend without a text here does not compile. Register: this app's audience.
export const EN_ERRORS: Record<ErrorCode, string> = {
  'error.unauthenticated': 'Your session has expired. Sign in again to continue.',
  'error.forbidden': 'You do not have permission for this operation.',
  'error.not_found': 'We could not find what you were looking for.',
  'error.conflict': 'The operation conflicts with the current state of the data.',
  'error.unprocessable': 'The data sent cannot be processed.',
  'error.internal': 'Internal error. Try again in a moment.',
  'error.http': 'Invalid request.',
  'error.validation_failed': 'Some fields are not filled in correctly.',
  'error.unique_violation': 'This value has already been used.',
  'error.foreign_key_violation': 'The reference given does not exist.',
  'error.check_violation': 'A value does not satisfy the constraints.',
  'error.not_null_violation': 'A required field is missing.',
  'error.constraint_violation': 'The data does not satisfy a constraint.',
  'error.identity_provider_unreachable':
    'The authentication service is not responding. Try again in a moment.',
  'auth.credentials_missing': 'Credentials missing. Sign in to continue.',
  'auth.token_invalid': 'Invalid session. Sign in again.',
  'auth.token_not_id': 'Invalid session. Sign in again.',
  'auth.account_not_linked': 'This account is not linked to any Volée profile.',
  'auth.account_deactivated': 'This account is deactivated. Contact your club.',
  'auth.staff_required': 'This area is reserved for Volée staff.',
  'auth.admin_required': 'This action is reserved for Volée administrators.',
  'auth.not_your_club': 'This club is not yours.',
  'auth.not_your_team': 'This team is not yours.',
  'auth.not_your_person': 'You may only correct your own details.',
  'auth.password_verification_required':
    'This needs your password again: enter it and try once more.',
  'auth.password_wrong': 'That is not your password.',
  'error.storage_unavailable': 'The file storage is not responding. Try again in a moment.',
  'club.not_found': 'Club not found.',
  'club.legal_name_taken': 'A club with this legal name already exists.',
  'club.lnd_name_taken': 'Another club already carries this federation name.',
  'club.logo_invalid_type': 'The logo must be a PNG, JPEG or WebP image.',
  'club.logo_too_large': 'The logo file is too large.',
  'registration.document_invalid_type': 'The document must be a PDF or an image.',
  'registration.document_too_large': 'The document file is too large.',
  'registration.document_unknown': 'That upload is no longer there. Choose the file again.',
  'registration.already_pending': 'A request for this address is already being reviewed.',
  'registration.already_decided': 'That request has already been decided.',
  'registration.not_awaiting_document': 'That request is not waiting for a document.',
  'club.logo_missing': 'This club has no logo.',
  'club.cover_invalid_type': 'The cover must be a PNG, JPEG or WebP image.',
  'club.cover_too_large': 'The cover file is too large.',
  'club.cover_missing': 'This club has no cover.',
  'club.field_reserved_to_staff': 'Volee changes this field, not the club.',
  'team_invite_link.already_active': 'This leva already has an active link: rotate it instead.',
  'team_invite_link.not_found':
    'This enrolment link is no longer valid. Ask whoever sent it for a new one.',
  'family_enrolment.already_pending': 'A request for this player is already being reviewed.',
  'family_enrolment.not_found': 'This request no longer exists.',
  'family_enrolment.already_decided': 'This request has already been reviewed.',
  'family_enrolment.guardian_confirmation_required':
    'Under 14, a request is approved only once a parent or guardian has confirmed it.',
  'family_enrolment.password_required': 'Choose a password for your access.',
  'family_enrolment.consent_required':
    'Tick the consent to the privacy notice to send the request.',
  'family_enrolment.verification_required':
    'Tick the declaration that you checked the data before confirming.',
  'family_enrolment.sign_in_required':
    'This e-mail already has a Volée account: sign in to continue.',
  'guardian_link.not_found': 'This link does not exist. Ask whoever sent it to send it again.',
  'guardian_link.expired':
    'This link has expired. Ask whoever sent it to make a new one from their page.',
  'guardian_link.closed': 'This request has already been answered.',
  'guardian_link.sign_in_required': 'This e-mail already has a Volée account: sign in to confirm.',
  'guardian_link.own_request':
    'You cannot confirm your own request: it takes a parent or guardian.',
  'guardian_link.consent_required': 'Tick the privacy notice to confirm.',
  'guardian_link.account_without_person':
    'Your account has no complete profile yet. Contact the club.',
  'guardian_link.guardian_under_age':
    'A guardian is an adult: this account cannot confirm. Sign in with a parent’s account.',
  'follower.not_found': 'This person no longer follows you.',
  'delegated_sign_in.unavailable':
    'A child cannot be added to this phone for now. Try again later.',
  'delegated_sign_in.no_account': 'Your child has no account of their own to add here yet.',
  'delegated_sign_in.rate_limited': 'You tried too many times in an hour. Try again later.',
  'guardian_link.child_access_required': 'Choose the username and the password for your child.',
  'family_enrolment.email_required':
    'From 14 you need an e-mail of your own: it is your access to Volée.',
  'family_enrolment.own_access_required':
    'They have turned 14 and have no e-mail of their own yet: approve once they have added one from their request page.',
  'account.username_invalid': 'Letters, digits and dots: from 4 to 20 characters.',
  'account.username_taken': 'This username is taken: choose another one.',
  'account.no_autonomy_notice': 'There is no notice to read for this account.',
  'account.own_email_required':
    'Add an e-mail of your own first: from 14 it is your access to Volée.',
  'child_access.not_found':
    'This child has no access with a username: their e-mail and password are their own.',
  'verification_code.invalid':
    'That code does not work: check you typed it right, or ask for a new one.',
  'verification_code.too_many': 'You asked for too many codes in an hour: try again later.',
  'account.email_in_use': 'This e-mail already has a Volée account: choose another one.',
  'account.has_a_mail': 'This account already has an e-mail.',
  'privacy_notice.not_found': 'The privacy notice is not available right now.',
  'parent_account.sign_in_required':
    'This e-mail already has a Volée account: sign in to continue.',
  'parent_account.consent_required': 'Tick the privacy notice to create your account.',
  'sign_up.persons_closed':
    'Players and parents join with an invitation or a link from their own club.',
  'kit.not_found': 'The club has no such kit.',
  'kit.second_needs_first': 'Save the first kit before the second.',
  'kit.first_needed_by_second': 'The first kit stays while there is a second one.',
  'kit.secondary_color_required': 'This design needs a second colour.',
  'kit.secondary_color_not_allowed': 'A solid shirt has no second colour.',
  'club.pitch_not_linked': 'This pitch is not one of the club’s.',
  // Teams are only written from the back office, so the last two cannot reach this app;
  // `team.not_found` can, the day a parent opens a link to a team that has been removed.
  'team.not_found': 'Team not found.',
  'team.already_exists': 'This club already fields that team.',
  'team.in_use': 'This team is still in use and cannot be removed.',
  'team.fields_locked_by_enrollments':
    'This team is entered in a league group this season: its age band, age limit and gender stay fixed while the entry is open. Write to Volée if it needs correcting.',
  'team.cover_invalid_type': 'The cover must be a PNG, JPEG or WebP image.',
  'team.cover_too_large': 'The cover file is too large.',
  'team.cover_missing': 'This team has no cover.',
  'pitch.not_found': 'Pitch not found.',
  'pitch.lnd_name_taken': 'Another pitch already carries this federation name.',
  'pitch.lnd_code_taken': 'This ground code already belongs to another pitch.',
  'bulletin_source.not_found': 'This source of comunicati no longer exists.',
  'bulletin_source.name_taken': 'A source with this name already exists.',
  'bulletin_source.url_taken': 'A source with this address already exists.',
  'bulletin_source.inactive': 'The source is switched off: switch it on before checking it.',
  'bulletin_check.running': 'A check is already running on this source.',
  'club.alias_taken': 'This name already belongs to a club.',
  'pitch.alias_taken': 'This name already belongs to a pitch.',
  'gateway.not_found': 'Recording device not found.',
  'gateway.hardware_id_taken': 'This recording device is already registered.',
  'gateway.hostname_taken': 'This recording device is already registered.',
  'gateway.placement_incomplete': 'Give both the pitch and the installation date.',
  'gateway.release_not_found': 'That recording device version is not available.',
  'gateway.release_untried': 'This version has not been tried on a device yet.',
  'gateway.secrets_unavailable': 'The device’s secrets are not available.',
  'gateway.maintenance_pending': 'This operation is not possible right now.',
  'gateway.retired': 'This operation is not possible right now.',
  'intervention.not_found': 'This operation is not possible right now.',
  'intervention.open': 'This operation is not possible right now.',
  'intervention.not_cancellable': 'This operation is not possible right now.',
  'bench.not_the_kits_board': 'This operation is not possible right now.',
  'bench.no_repair_pending': 'This operation is not possible right now.',
  'gateway.silent': 'This operation is not possible right now.',
  'intervention.parts_mismatch': 'This operation is not possible right now.',
  'bench.tailscale_unavailable': 'The remote access service is not answering.',
  'bench.box_not_found': 'Device not found.',
  'bench.box_busy': 'This device is already busy.',
  'bench.router_code_mismatch': 'This code does not match the device’s router.',
  'bench.job_not_found': 'Job not found.',
  'bench.job_not_failed': 'There is nothing to retry.',
  'bench.job_wrong_state': 'This operation is not possible right now.',
  'bench.hello_too_large': 'The request is too large.',
  'bench.full': 'Too many devices right now.',
  'bench.not_configured': 'This service is not available.',
  'bench.no_open_job': 'This operation is not possible right now.',
  'camera.not_found': 'Camera not found.',
  'recording.not_found': 'Recording not found.',
  'camera.mac_address_taken': 'This camera is already registered.',
  'camera.serial_number_taken': 'This camera is already registered.',
  'camera.uid_taken': 'This camera is already registered.',
  'player.not_found': 'Player not found.',
  'person.not_found': 'Person not found.',
  'person.already_exists': 'Someone with this name and date of birth is already on file.',
  'person.tax_code_taken': 'That codice fiscale is already on file for a different person.',
  'person.tax_code_conflicts': 'This person is already on file with a different codice fiscale.',
  'person.field_reserved_to_staff': 'This detail is your club’s to correct, not yours.',
  'player.field_reserved_to_the_club': 'Your club decides this, not you.',
  'player.field_reserved_to_the_player': 'Only the player decides this.',
  'player.photo_invalid_type': 'The photo must be a PNG, JPEG or WebP image.',
  'player.photo_too_large': 'The photo file is too large.',
  'player.photo_missing': 'This player has no photo.',
  'staff.photo_invalid_type': 'The photo must be a PNG, JPEG or WebP image.',
  'staff.photo_too_large': 'The photo file is too large.',
  'staff.photo_missing': 'There is no photo to remove.',
  'profile.not_public': 'This player does not show their profile.',
  'registration.not_found': 'Registration not found.',
  'registration.player_already_registered':
    'Already registered for this season. A release from the previous club is needed first.',
  'registration.after_season_end': 'The registration date falls after this season ended.',
  'registration.release_before_registration':
    'The release date cannot come before the registration date.',
  'season.not_found': 'That season is not in the calendar.',
  'season.bounds_conflict': 'Rows in this season are dated after that boundary.',
  'season.overlaps': 'Those dates overlap another season.',
  'season.dates_out_of_order': 'A season cannot end before it starts.',
  // The league endpoints are staff-only; these can only reach this app indirectly, e.g. a
  // shared component surfacing a generic backend error.
  'league_category.not_found': 'Category not found.',
  'league_category.already_exists': 'This category already exists.',
  'league_category.in_use': 'This category is still in use and cannot be removed.',
  'league_category.fields_locked_by_groups': 'These fields can no longer be changed.',
  'league_group.not_found': 'League group not found.',
  'league_group.already_exists': 'This league group already exists.',
  'league_group.in_use': 'This league group is still in use and cannot be removed.',
  'enrollment.not_found': 'Enrollment not found.',
  'enrollment.already_exists': 'This team is already enrolled.',
  'enrollment.team_category_mismatch': 'This team does not match the league group.',
  'enrollment.in_use': 'This enrollment is still in use and cannot be removed.',

  // No screen here produces these yet — the calendar is the back-office's. They are in the
  // catalogue because `Record<ErrorCode, string>` is what keeps a missing one from shipping.
  'match.not_found': 'Fixture not found.',
  'match.already_exists': 'These two teams already meet in this leg.',
  'match.in_use': 'This fixture has a recording attached, so it cannot be deleted.',
  'match.sides_equal': 'A team cannot play itself.',
  'match.side_not_in_group': 'This team is not entered in this league group.',
  'match.side_withdrawn': 'This team has withdrawn and cannot be given a new fixture.',
  'match.side_already_plays_that_matchday': 'This team already plays on matchday {matchday}.',
  'match.already_played': 'This fixture has been played: it cannot be deleted or rescheduled.',
  'match.kickoff_before_season': 'The kick-off is before the season opens.',
  'team_sheet.side_not_in_match': 'This side does not belong to this match.',
  'team_sheet.shirt_duplicated': 'Two called-up players share the same shirt number.',
  'team_sheet.too_many_starters': 'No more than 11 starters are allowed.',
  'team_sheet.too_many_call_ups': 'No more than 20 players can be called up.',
  'team_sheet.captaincy_duplicated': 'The captain or vice-captain role is assigned twice.',
  'team_sheet.no_call_ups': 'Nobody is called up: publishing needs at least one player.',
  'team_sheet.call_ups_locked':
    'The match has started: a published call-up no longer changes who is called up, only the numbers.',
  'team_sheet.details_locked':
    'The match has started: the meeting and the notes of the call-up can no longer be changed.',
  'team_sheet.locked_by_analysis':
    'The call-up has already gone to the analysis: the numbers can no longer be changed.',
  'team_sheet.call_up_not_found': 'This player is not called up for this match.',
  'team_sheet.not_published':
    'The call-up is not published yet: there is nothing to answer for now.',
  'team_sheet.answers_closed': 'The match has started: the answer can no longer be changed.',
  'team_sheet.registration_not_in_club': 'This person is not registered with this club.',
  'team_sheet.registration_repeated': 'The same registrant is called up twice.',
  'team_sheet.club_kit_missing':
    'The club does not have that kit: choose another one for this match.',
  'team_sheet.team_kit_missing':
    'The squad does not have that kit: choose another one for this match.',
  'team_sheet.meeting_place_without_time':
    'A meeting place needs a meeting time: add the time, or remove the place.',
  'appointment.already_exists': 'This appointment is already assigned.',
  'appointment.not_found': 'Appointment not found.',
  'appointment.already_ended': 'This appointment has already ended.',
  'appointment.role_forbids_team': 'This role is club-wide.',
  'appointment.field_reserved_to_staff': 'Only Volée staff can change this.',
  'appointment.last_official':
    'The club would be left with no club officials: Volée closes this one.',
  'appointment.end_after_season': 'That date falls after the season has ended.',
  'appointment.seats_full': 'The two team seats are taken: free one up.',
  'appointment.officials_full': 'The two club seats are taken: free one up.',
  'appointment.seat_without_team': 'To give a team seat, choose the team.',
  'appointment.not_on_a_team': 'This appointment is not on a team.',
  'appointment.seat_already_held': 'This person already holds a seat on this team.',
  'appointment.official_takes_no_team_seat': 'A club official does not take a team seat.',
  'team.not_in_club': 'That team belongs to another club.',
  'team.player_already_in_roster': 'This player is already in the roster.',
  'team.player_not_in_roster': 'This player is not in the roster.',
  'registration.not_in_club': 'This player is not registered with your club.',
  'registration.released': 'This player has been released and cannot join a roster.',
  'registration.not_in_season': 'This registration belongs to another season.',
  'registration.repeated': 'The same player is in the selection twice.',
  'registration.already_in_a_team':
    'This registrant is already in another squad’s roster: take them out of it first.',
  'appointment.person_plays': 'This person plays.',
  'registration.person_is_official': 'This person holds an incarico.',
  'invitation.not_found': 'Invitation invalid or expired.',
  // Four states the screens must tell apart: "not a valid link" and "no longer valid" are
  // different sentences, and the last three each have their own way out.
  'invitation.token_unknown': 'This link is not valid.',
  'invitation.expired': 'This invitation has expired.',
  'invitation.already_accepted': 'This invitation has already been used.',
  'invitation.revoked': 'This invitation is no longer valid.',
  'invitation.already_settled': 'This invitation has already been used.',
  'invitation.already_pending':
    'There is already a pending invitation for this address and this role: resend it from the list.',
  'invitation.person_id_reserved': 'Only Volée can pick a person from the registry.',
  'invitation.email_not_the_account':
    'This person already has an account: the invitation goes to their address.',
  'invitation.team_required': 'You invite onto a seat only: choose the team.',
  'invitation.sign_in_required':
    'This address already has a Volée account: sign in to accept the incarico.',
  'invitation.other_account':
    'The invitation is for another account: sign out and sign in with the address it was sent to.',
  'invitation.no_account': 'This address has no account yet: fill in your details to create one.',
  'activation.person_has_account':
    'An account with this name and date of birth already exists. If it is yours, sign in with it and ask whoever invited you to use its address.',
  'activation.person_at_another_address':
    'The registry already has a person with this name and date of birth. Ask whoever invited you to do it from your record, or ask Volée.',
  // The two staff.* codes cannot reach this app — staff is invited from the back office —
  // but account.email_taken can, from any future app_user INSERT.
  'account.not_found': 'This account does not exist.',
  'account.no_identity': 'This account has no sign-in identity yet.',
  'account.email_taken': 'A Volée account with this email already exists.',
  'account.bound_to_another_person':
    'That address is already the account of another person: invite them at the address they use in Volée, or ask Volée to sort it out.',
  'account.person_already_bound':
    'This person already has a Volée account at a different address: use that one, or ask Volée to move the account.',
  'account.identity_already_exists': 'A Volée account with this email already exists.',
  'account.password_rejected': 'This password does not meet the requirements.',
  'staff.identity_already_exists': 'This email already exists in the identity provider.',
  'staff.invite_quota_exceeded': 'The invitation email could not be sent.',
  'subscription.not_a_player': 'Only a registered player can be subscribed.',
  'subscription.not_a_guardian':
    'Only the player themselves or one of their parents can subscribe them.',
  'subscription.guardian_pays': 'Under 14, a parent or guardian of yours starts the plan.',
  'subscription.already_active': 'This player is already subscribed.',
  'subscription.not_the_payer': 'Only the person paying for the subscription can change it.',
  'subscription.not_active': 'This plan is no longer active.',
  'billing.provider_unavailable': 'The payment service is not responding. Try again shortly.',
  'match_analysis.no_video': 'This match cannot be sent yet: no video of it has been uploaded.',
  'match_analysis.no_published_team_sheet':
    'This match cannot be sent yet: neither team has published its call-up.',
  'match_analysis.provider_unavailable':
    'The match analysis service is not responding. Try again shortly.',
  'match_analysis.switch_unavailable':
    'Sending for analysis is off for this environment and cannot be turned on from here.',
  'match_analysis.provider_refused':
    'The match analysis service refused this match. The details are in the logs.',
  'match_analysis.unknown_match_format':
    'This match cannot be sent: the category of the home team has no known match format (players and periods).',
  'category.not_admitted':
    'This category is not one Volée admits: pick it from the list. The female categories are Under 15, Under 17, Under 19 and Seniores.',
  'highlights.not_your_library':
    'Only the player and their guardians change what is featured, the favourites and the playlists.',
  'highlights.clip_not_found': 'This clip is no longer there.',
  'highlights.playlist_not_found': 'This playlist is no longer there.',
  'highlights.plan_required': 'The showcase, favourites and playlists need the plan.',
  'webhook.bad_signature': 'Invalid webhook signature.',
}

/** Shown when the backend sends a `code` this catalogue does not know. */
export const EN_UNKNOWN_ERROR = 'Something went wrong. Try again in a moment.'

/** Shown when the network never answered: there is no `code` to translate. */
export const EN_NETWORK_ERROR = 'No connection available. Check the network and try again.'
